import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton
} from "@velaro/velaro-component-lib";
import Select from "@/components/Select";
import useApi from "@/hooks/useApi";
import React, { useMemo, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";

interface Props {
  onClose(): void;
  onSuccess(): void;
}

interface PhoneNumber {
  friendly_name: string;
  phone_number: string;
}

type Tab = "buy" | "port" | "shortcode";
type BuyType = "local" | "tollfree";
type BuyStep = "lookup" | "purchase";
type PortStep = "form" | "submitted";

const errorMap: Record<string, string> = {
  sms_error: "An error occurred while purchasing.",
  number_not_found: "No phone number found for the selected area code.",
  invalid_area_code: "Invalid area code.",
  port_error: "An error occurred while submitting the porting request.",
  invalid_phone: "Please enter a valid 10-digit US phone number."
};

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-blue-600 text-blue-600"
          : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

export default function SmsIntegrationSetupModal(props: Props) {
  const [tab, setTab] = useState<Tab>("buy");

  // Buy tab state
  const [buyType, setBuyType] = useState<BuyType>("local");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [buyStep, setBuyStep] = useState<BuyStep>("lookup");
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[] | null>(null);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");

  // Port tab state
  const [portPhone, setPortPhone] = useState("");
  const [portCarrier, setPortCarrier] = useState("");
  const [portAccountNumber, setPortAccountNumber] = useState("");
  const [portPin, setPortPin] = useState("");
  const [portTeam, setPortTeam] = useState("");
  const [portLoading, setPortLoading] = useState(false);
  const [portError, setPortError] = useState("");
  const [portStep, setPortStep] = useState<PortStep>("form");

  const api = useApi();

  // Buy tab logic
  const buyButtonText = useMemo(() => {
    if (buyStep === "lookup" && !loading) return "Find Numbers";
    if (buyStep === "lookup" && loading) return "Searching...";
    if (buyStep === "purchase" && !loading) return "Purchase Number";
    if (buyStep === "purchase" && loading) return "Purchasing...";
  }, [loading, buyStep]);

  const phoneNumberOptions = useMemo(() => {
    const options: Record<string, string> = {};
    if (!phoneNumbers) return options;
    for (const num of phoneNumbers) {
      options[num.phone_number] = num.friendly_name;
    }
    return options;
  }, [phoneNumbers]);

  const fetchAvailablePhoneNumbers = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.messaging.post("Sms/Integrations/GetPhoneNumbers", {
        countryCode: "US",
        areaCode: buyType === "local" ? areaCode : "",
        numberType: buyType
      });
      const numbers: PhoneNumber[] = await response.json();
      if (!Array.isArray(numbers) || numbers.length === 0) {
        setError(errorMap["number_not_found"]);
        setPhoneNumbers([]);
      } else {
        setSelectedNumber(numbers[0].phone_number);
        setPhoneNumbers(numbers);
      }
    } catch {
      setError(errorMap["sms_error"]);
    } finally {
      setBuyStep("purchase");
      setLoading(false);
    }
  }, [api.messaging, areaCode, buyType]);

  async function submitPurchase() {
    setError("");
    setLoading(true);
    try {
      const resp = await api.messaging.post("Sms/Integrations", {
        phoneNumber: selectedNumber,
        teamId: selectedTeam
      });
      if (resp.ok) {
        props.onSuccess();
      } else {
        const errKey: string = await resp.json().catch(() => "sms_error");
        setError(errorMap[errKey] ?? errorMap["sms_error"]);
      }
    } catch {
      setError(errorMap["sms_error"]);
    } finally {
      setLoading(false);
    }
  }

  function isValidAreaCode(code: string) {
    return /^\d{3}$/.test(code);
  }

  function isValidUSPhone(phone: string) {
    return /^\+?1?\d{10}$/.test(phone.replace(/[\s\-().]/g, ""));
  }

  function normalizePhone(phone: string) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return phone;
  }

  async function submitPort() {
    setPortError("");
    if (!isValidUSPhone(portPhone)) {
      setPortError(errorMap["invalid_phone"]);
      return;
    }
    setPortLoading(true);
    try {
      const resp = await api.messaging.post("Sms/Integrations/RequestPort", {
        phoneNumber: normalizePhone(portPhone),
        currentCarrier: portCarrier,
        accountNumber: portAccountNumber,
        pin: portPin,
        teamId: portTeam || null
      });
      if (resp.ok) {
        setPortStep("submitted");
      } else {
        setPortError(errorMap["port_error"]);
      }
    } catch {
      setPortError(errorMap["port_error"]);
    } finally {
      setPortLoading(false);
    }
  }

  return (
    <Modal show={true}>
      <ModalHeader title="Add Phone Number" onClose={props.onClose} />

      {/* Tabs */}
      <div className="flex border-b border-slate-200 px-4 pt-4">
        <TabButton active={tab === "buy"} onClick={() => setTab("buy")}>
          Buy a Number
        </TabButton>
        <TabButton active={tab === "port"} onClick={() => setTab("port")}>
          Port a Number
        </TabButton>
        <TabButton active={tab === "shortcode"} onClick={() => setTab("shortcode")}>
          Short Codes
        </TabButton>
      </div>

      {/* ── BUY TAB ── */}
      {tab === "buy" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (buyStep === "lookup") {
              if (buyType === "local" && !isValidAreaCode(areaCode)) {
                setError(errorMap["invalid_area_code"]);
              } else {
                setError("");
                fetchAvailablePhoneNumbers();
              }
            } else {
              submitPurchase();
            }
          }}
        >
          <ModalBody>
            <div className="p-4 space-y-4">
              {/* Number type selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Number Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setBuyType("local");
                      setBuyStep("lookup");
                      setPhoneNumbers(null);
                      setError("");
                    }}
                    className={`border rounded-lg p-3 text-left transition-colors ${
                      buyType === "local"
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="font-medium text-sm text-slate-800">Local (10DLC)</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Standard 10-digit US number. Best for conversational SMS. ~$1/mo.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBuyType("tollfree");
                      setBuyStep("lookup");
                      setPhoneNumbers(null);
                      setAreaCode("");
                      setError("");
                    }}
                    className={`border rounded-lg p-3 text-left transition-colors ${
                      buyType === "tollfree"
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="font-medium text-sm text-slate-800">Toll-Free</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      800/833/844/855/866/877/888 numbers. High throughput. ~$2/mo.
                    </div>
                  </button>
                </div>
              </div>

              {/* Team */}
              <div className="flex items-center gap-3">
                <label className="w-1/3 text-sm font-medium text-slate-700">Route to Team</label>
                <TeamSelectDeprecated
                  id="sms-buy-team"
                  className="input w-2/3"
                  onChange={(e) => setSelectedTeam(e.currentTarget.value)}
                  value={selectedTeam}
                />
              </div>

              {/* Area code (local only) */}
              {buyType === "local" && (
                <div className="flex items-center gap-3">
                  <label className="w-1/3 text-sm font-medium text-slate-700">Area Code</label>
                  <input
                    className="input w-2/3"
                    onChange={(e) => {
                      setAreaCode(e.target.value);
                      setBuyStep("lookup");
                    }}
                    placeholder="e.g. 415"
                    maxLength={3}
                    value={areaCode}
                  />
                </div>
              )}

              {/* Number picker */}
              {buyStep === "purchase" && phoneNumbers && phoneNumbers.length > 0 && (
                <div className="flex items-center gap-3">
                  <label className="w-1/3 text-sm font-medium text-slate-700">Select Number</label>
                  <Select
                    className="input w-2/3"
                    onChange={(value) => setSelectedNumber(value)}
                    value={selectedNumber}
                    options={phoneNumberOptions}
                  />
                </div>
              )}

              {error && <div className="text-red-600 text-sm">{error}</div>}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="p-4 flex justify-end">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <LoadingSpinner /> {buyStep === "lookup" ? "Searching..." : "Purchasing..."}
                </div>
              ) : (
                <PrimaryBlueButton type="submit" label={buyButtonText} />
              )}
            </div>
          </ModalFooter>
        </form>
      )}

      {/* ── PORT TAB ── */}
      {tab === "port" && (
        <>
          <ModalBody>
            <div className="p-4 space-y-4">
              {portStep === "submitted" ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-check text-green-600 text-lg" aria-hidden="true" />
                  </div>
                  <p className="font-medium text-slate-800">Porting request submitted!</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Our team will contact your current carrier to initiate the port. The process
                    typically takes <strong>5–10 business days</strong>. You'll receive an email
                    confirmation once the port is complete.
                  </p>
                  <p className="text-xs text-slate-400 mt-4">
                    Continue using your existing number until the port completes — calls and texts
                    will not be interrupted.
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <span className="font-semibold">What is number porting?</span> You can move
                    your existing business phone number from your current carrier to Velaro's Twilio
                    account. The number remains yours — it just gets routed through Velaro for SMS.
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="w-1/3 text-sm font-medium text-slate-700">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="input w-2/3"
                      placeholder="+1 (555) 000-0000"
                      value={portPhone}
                      onChange={(e) => setPortPhone(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="w-1/3 text-sm font-medium text-slate-700">
                      Current Carrier
                    </label>
                    <input
                      className="input w-2/3"
                      placeholder="e.g. AT&T, Verizon, T-Mobile"
                      value={portCarrier}
                      onChange={(e) => setPortCarrier(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="w-1/3 text-sm font-medium text-slate-700">
                      Account Number
                    </label>
                    <input
                      className="input w-2/3"
                      placeholder="Carrier account number"
                      value={portAccountNumber}
                      onChange={(e) => setPortAccountNumber(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="w-1/3 text-sm font-medium text-slate-700">
                      PIN / Password
                    </label>
                    <input
                      className="input w-2/3"
                      placeholder="Transfer PIN (if required)"
                      value={portPin}
                      onChange={(e) => setPortPin(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="w-1/3 text-sm font-medium text-slate-700">Route to Team</label>
                    <TeamSelectDeprecated
                      id="sms-port-team"
                      className="input w-2/3"
                      onChange={(e) => setPortTeam(e.currentTarget.value)}
                      value={portTeam}
                    />
                  </div>

                  {portError && <div className="text-red-600 text-sm">{portError}</div>}

                  <p className="text-xs text-slate-400">
                    By submitting, you authorize Velaro to initiate the porting process on your
                    behalf. A Letter of Authorization (LOA) may be required from your current
                    carrier.
                  </p>
                </>
              )}
            </div>
          </ModalBody>
          {portStep === "form" && (
            <ModalFooter>
              <div className="p-4 flex justify-end">
                {portLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <LoadingSpinner /> Submitting...
                  </div>
                ) : (
                  <PrimaryBlueButton
                    label="Submit Porting Request"
                    onClick={submitPort}
                  />
                )}
              </div>
            </ModalFooter>
          )}
          {portStep === "submitted" && (
            <ModalFooter>
              <div className="p-4 flex justify-end">
                <PrimaryBlueButton label="Close" onClick={props.onClose} />
              </div>
            </ModalFooter>
          )}
        </>
      )}

      {/* ── SHORT CODE TAB ── */}
      {tab === "shortcode" && (
        <>
          <ModalBody>
            <div className="p-4 space-y-5">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    Short Code
                  </span>
                  <span className="font-semibold text-slate-800">5–6 digit numbers</span>
                </div>
                <p className="text-sm text-slate-700">
                  Short codes (e.g. <strong>55555</strong>) are the highest-throughput SMS option —
                  up to <strong>500 messages per second</strong>. They're fully carrier-approved and
                  bypass spam filters, making them ideal for high-volume marketing, alerts, and
                  2FA/OTP.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-slate-800 text-sm">What's required</h4>
                <div className="space-y-2">
                  {[
                    {
                      icon: "fa-clock",
                      color: "text-amber-500",
                      label: "Provisioning time",
                      value: "8–12 weeks (carrier approval required)"
                    },
                    {
                      icon: "fa-dollar-sign",
                      color: "text-emerald-500",
                      label: "Cost",
                      value: "~$1,500/month (dedicated) or ~$300/month (shared)"
                    },
                    {
                      icon: "fa-file-lines",
                      color: "text-blue-500",
                      label: "Use case approval",
                      value: "Carriers review your messaging use case and content"
                    },
                    {
                      icon: "fa-building",
                      color: "text-slate-500",
                      label: "Business verification",
                      value: "EIN and company documentation required"
                    }
                  ].map((item) => (
                    <div key={item.label} className="flex gap-3 items-start">
                      <i
                        className={`fas ${item.icon} ${item.color} mt-0.5 w-4 flex-shrink-0`}
                        aria-hidden="true"
                      />
                      <div className="text-sm">
                        <span className="font-medium text-slate-700">{item.label}: </span>
                        <span className="text-slate-600">{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-1">
                  Need high-volume SMS without the wait?
                </p>
                <p className="text-sm text-blue-700">
                  Consider a <strong>toll-free number</strong> (provisioned instantly, supports{" "}
                  ~3 messages/second) or an <strong>alphanumeric sender ID</strong> for one-way
                  notifications.
                </p>
              </div>

              <div className="border border-slate-200 rounded-lg p-4">
                <p className="text-sm text-slate-700 mb-3">
                  To request a short code, contact Velaro support with your use case details. We'll
                  work with Twilio to submit the carrier application on your behalf.
                </p>
                <a
                  href="mailto:support@velaro.com?subject=Short Code Request"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                >
                  <i className="fas fa-envelope" aria-hidden="true" />
                  Contact support to request a short code
                </a>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="p-4 flex justify-end">
              <button
                type="button"
                onClick={props.onClose}
                className="text-sm text-slate-600 hover:text-slate-800 underline"
              >
                Close
              </button>
            </div>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
